import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import {
  buildPaginatedEndpoint,
  buildSortedEndpoint,
  convertDatesInObject,
} from '@/helpers/api'

import { tasksQueryKeys } from '@/hooks/tasks/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type {
  PaginateRequest,
  PaginatedResponse,
  SortRequest,
} from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Task, TaskFilter } from '@/types/task/Task'

export interface UseUserTasksArgs {
  orgId: UUID | ''
  filter?: TaskFilter
  pagination?: PaginateRequest
  sort?: SortRequest
}

export const DEFAULT_FILTER: TaskFilter = {}

export const DEFAULT_SORT: SortRequest = {
  order_by: 'due_date',
  sort: 'desc',
}

export const DEFAULT_PAGINATION: PaginateRequest = {
  limit: 10,
}

interface FetchTasksArgs {
  orgId: UUID | ''
  filter: TaskFilter
  pagination: PaginateRequest
  sort: SortRequest
  accessToken: string
}

export const fetchUserTasks = async (
  args: FetchTasksArgs,
): Promise<PaginatedResponse<Task>> => {
  const { orgId, filter, pagination, sort, accessToken } = args

  if (!orgId) {
    throw new Error('Org ID is required')
  }

  if (accessToken === null || accessToken === undefined) {
    return { count: 0, has_more: false, data: [] }
  }

  let url = buildPaginatedEndpoint(`${API_URL}/api/v1/tasks`, pagination)
  url = buildSortedEndpoint(url, sort)

  if (filter.status) {
    url = `${url}${url.includes('?') ? '&' : '?'}status=${filter.status.join('&status=')}`
  }

  if (orgId) {
    url = `${url}${url.includes('?') ? '&' : '?'}organization_id=${orgId}`
  }

  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json()) as PaginatedResponse<Task>
}

const select = (data: PaginatedResponse<Task>) => {
  return {
    data: data.data,
    count: data.count,
    has_more: data.has_more,
  }
}

export const useUserTasks = ({
  orgId,
  filter = DEFAULT_FILTER,
  pagination = DEFAULT_PAGINATION,
  sort = DEFAULT_SORT,
}: UseUserTasksArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const query = useQuery({
    queryFn: () =>
      fetchUserTasks({
        orgId,
        filter,
        pagination,
        sort,
        accessToken,
      }),
    queryKey: tasksQueryKeys.user({
      orgId: orgId,
      status: filter.status ?? [],
      limit: pagination.limit,
      offset: pagination.offset ?? 0,
      order_by: sort.order_by,
      sort: sort.sort,
    }),
    // surfaces without an active org (e.g. the all-orgs overview) simply
    // render no user tasks instead of erroring
    enabled: orgId !== '',
    // never goes stale on its own; task mutations invalidate ['tasks','user']
    // explicitly, and the default refetchOnMount picks that up on remount
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    select,
  })
  return query
}
